import { DeleteOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  Divider,
  Empty,
  Flex,
  Form,
  List,
  SelectProps,
  Spin
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  useIdentityServiceGetProfilesListEndpointKey,
  useInstitutionServiceGetInstitutionDepartmentUserListEndpoint
} from 'src/api/queries'
import { GetProfilesListResponseItem, IdentityService } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Select } from 'src/components'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type Props = {
  form: FormInstance
  institutionId?: string
  departmentId?: string
} & SelectProps

const DEFAULT_USER_LIST_PER_PAGE = 5

export const UsersSearch = ({
  institutionId,
  departmentId,
  form,
  ...rest
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const [pageSize] = useState(DEFAULT_USER_LIST_PER_PAGE)

  const [selectedPage, setSelectedPage] = useState(1)

  const selectedUsers = Form.useWatch('userProfileIds', form) || []

  const [selectedUsersList, setSelectedUsersList] = useState<
    { value?: string; label?: string }[]
  >([])

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [useIdentityServiceGetProfilesListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await IdentityService.getProfilesListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetProfilesListResponseItem[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!institutionId
  })

  const userOptions = useMemo(() => {
    return data?.pages.flatMap(
      (page) =>
        page.data?.map((user) => ({
          value: user.id,
          label: `${user.firstName} ${user.lastName}`
        })) || []
    )
  }, [data])

  const { data: departmentUsers, isLoading: departmentUsersLoading } =
    useInstitutionServiceGetInstitutionDepartmentUserListEndpoint(
      {
        id: String(institutionId),
        departmentId: String(departmentId)
      },
      [String(departmentId), String(institutionId)],
      {
        enabled: !!departmentId && !!institutionId
      }
    )

  const onSelect = (value: string) => {
    const newUser = userOptions?.find((user) => user?.value === value)
    if (newUser) {
      setSelectedUsersList((prev) => [...prev, newUser])
      form.setFieldsValue({
        userProfileIds: [...selectedUsers, value]
      })
    }
    setSearch('')
  }

  const onDeselect = (value?: string) => {
    setSelectedUsersList((prev) => prev?.filter((user) => user.value !== value))
    const updatedUsers = selectedUsers.filter(
      (userId: string) => userId !== value
    )
    form.setFieldsValue({ userProfileIds: updatedUsers })
    setSearch('')
    setSelectedPage(1)
  }

  const updateSelectedUsersList = useCallback(() => {
    if (departmentId && departmentUsers?.data) {
      const departmentUserIds = departmentUsers.data.map(
        (option) => option?.institutionDepartmentUser?.id
      )

      const currentUserProfiles = departmentUserIds
        .map((id) => userOptions?.find((user) => user?.value === id))
        .filter((user): user is { value: string; label: string } => !!user)

      setSelectedUsersList((prev) => [
        ...new Set([...prev, ...(currentUserProfiles || [])])
      ])

      form.setFieldsValue({
        userProfileIds: Array.from(
          new Set([...departmentUserIds, ...selectedUsers])
        )
      })
    }
  }, [departmentId, departmentUsers?.data, form])

  useEffect(() => {
    updateSelectedUsersList()
  }, [departmentId, departmentUsers?.data, form, updateSelectedUsersList])

  const paginatedSelectedUsers = selectedUsersList.slice(
    (selectedPage - 1) * pageSize,
    selectedPage * pageSize
  )

  return (
    <>
      <Select
        filterOption={false}
        options={userOptions}
        onSearch={(value) => setSearch(value)}
        mode="multiple"
        maxTagCount={2}
        onClear={() => setSearch('')}
        loading={isFetching}
        placeholder={t('common.user')}
        showSearch={true}
        value={selectedUsers}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onPopupScroll={(e) => {
          const target = e.target as HTMLElement
          if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            fetchNextPage()
          }
        }}
        notFoundContent={
          isFetching ? (
            <Flex justify="center">
              <Spin spinning />
            </Flex>
          ) : (
            <Empty />
          )
        }
        {...rest}
      />

      {!!selectedUsersList?.length && (
        <>
          <Divider />
          <List
            className={styles.listItem}
            itemLayout="horizontal"
            dataSource={paginatedSelectedUsers}
            loading={departmentUsersLoading}
            pagination={{
              current: selectedPage,
              pageSize,
              total: selectedUsersList.length,
              showTotal: (total) => `${t('common.total')} ${total}`,
              onChange: (page) => setSelectedPage(page),
              hideOnSinglePage: true
            }}
            renderItem={(user) => (
              <List.Item
                key={user.value}
                actions={[
                  <Button
                    key={user.value}
                    icon={<DeleteOutlined />}
                    onClick={() => onDeselect(user?.value)}
                    danger
                  />
                ]}
              >
                <List.Item.Meta title={user.label} />
              </List.Item>
            )}
          />
        </>
      )}
    </>
  )
}
